import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { TeamInLeague, Player, TacticalPosition, Tactics, TacticalMentality, AttackingFocus, PassingStyle, DefensiveStyle, TacklingStyle, GameState, Position, NewsItem } from '../../domain/entities';
import { FORMATION_OPTIONS, FORMATIONS, POSITION_MAP, getFormationZones, TACTICAL_POSITION_DETAILS, calculateStarRating, ASSISTANT_MANAGER_LEVELS } from '../../domain/constants';
import { useAssistantManagerManagement } from '../../application/hooks/management/useAssistantManagerManagement';
import { useToast } from '../../contexts/ToastContext';
import { PlayerFace } from '../shared/PlayerFace';
import { soundService } from '../../services/SoundService';
import { AdPlaceholder } from '../shared/AdPlaceholder';
import { View } from '../../App';
import './TacticsScreen.css';
import { PitchBackground } from '../shared/PitchBackground';
import { FitnessBadge } from '../shared/FitnessBadge';
import { getSquadSelectionSuggestions, SquadSuggestion } from '../../services/TeamService';
import { MoraleBadge } from '../shared/MoraleBadge';
import { calculateMatchPerformance, calculatePositionalPenalty } from '../../utils/utilities';
import { MatchPerformanceDetailModal } from '../shared/MatchPerformanceDetailModal';
import { PlayerAssignmentModal } from '../modals/PlayerAssignmentModal';
import { TacticsSetupHelpModal } from '../shared/TacticsSetupHelpModal';
import { TeamSetupHelpModal } from '../shared/TeamSetupHelpModal';
import { calculateTeamStrengths } from '../../services/MatchSimulator';
import { CollapsibleCard } from '../shared/CollapsibleCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { iconMap } from '../../utils/iconUtils';
import { faChess, faAmbulance } from '@fortawesome/free-solid-svg-icons';

interface TacticsScreenProps {
    team: TeamInLeague;
    onUpdateTactics: (formation: string, mentality: TacticalMentality, lineup: Tactics['lineup'], attackingFocus: AttackingFocus, passingStyle: PassingStyle, defensiveStyle: DefensiveStyle, tacklingStyle: TacklingStyle) => void;
    onAddNews: (newsItem: NewsItem) => void;
    adsEnabled: boolean;
    currentWeek: number;
    pushView: (view: View) => void;
    gameState: GameState;
}

const getLastName = (fullName: string): string => {
    if (!fullName) return '';
    const parts = fullName.trim().split(' ');
    return parts[parts.length - 1];
};

const calculateBestLineup = (formation: string, squad: Player[]): Tactics['lineup'] => {
    const lineup: Tactics['lineup'] = {};
    const formationPositions = FORMATIONS[formation];
    if (!formationPositions) return {};

    const availablePlayers = [...squad].filter(p => p.injury?.type !== 'Severe' && !p.suspension);

    // Helper function to calculate enhanced performance (same as suggestions algorithm)
    const calculateEnhancedPerformance = (player: Player): number => {
        const basePerf = calculateMatchPerformance(player);
        // Morale modifier: Ecstatic(+3), Happy(+1.5), Content(0), Unhappy(-2), Furious(-4)
        const moraleModifiers: Record<string, number> = {
            'Ecstatic': 3,
            'Happy': 1.5,
            'Content': 0,
            'Unhappy': -2,
            'Furious': -4
        };
        const moraleBonus = moraleModifiers[player.morale] || 0;
        return basePerf + moraleBonus;
    };

    // Helper function to calculate final performance for a position
    const calculateFinalPerformance = (player: Player, position: TacticalPosition): number => {
        const enhancedPerf = calculateEnhancedPerformance(player);
        const penalty = calculatePositionalPenalty(player, position);
        return enhancedPerf * (1 - penalty);
    };

    // Assign each position to the best available player based on final performance
    formationPositions.forEach(pos => {
        const requiredPosition = POSITION_MAP[pos];
        let bestPlayerIndex = -1;
        let bestScore = -Infinity;

        // Only consider players who match the required position
        availablePlayers.forEach((player, index) => {
            if (player.position === requiredPosition) {
                const finalPerf = calculateFinalPerformance(player, pos);
                if (finalPerf > bestScore) {
                    bestScore = finalPerf;
                    bestPlayerIndex = index;
                }
            }
        });

        if (bestPlayerIndex !== -1) {
            lineup[pos] = availablePlayers[bestPlayerIndex].id;
            availablePlayers.splice(bestPlayerIndex, 1);
        } else {
            lineup[pos] = null;
        }
    });

    return lineup;
};


export const TacticsScreen = ({ team, onUpdateTactics, onAddNews, adsEnabled, currentWeek, pushView, gameState }: TacticsScreenProps) => {
    const { addToast } = useToast();
    const [formation, setFormation] = useState(team.tactics.formation);
    const [mentality, setMentality] = useState<TacticalMentality>(team.tactics.mentality || 'Balanced');
    const [lineup, setLineup] = useState<Tactics['lineup']>(team.tactics.lineup);
    const [selectedSlot, setSelectedSlot] = useState<TacticalPosition | null>(null);
    const [activeList, setActiveList] = useState<'xi' | 'reserves'>('xi');
    const [attackingFocus, setAttackingFocus] = useState<AttackingFocus>(team.tactics.attackingFocus || 'Mixed');
    const [passingStyle, setPassingStyle] = useState<PassingStyle>(team.tactics.passingStyle || 'Mixed');
    const [defensiveStyle, setDefensiveStyle] = useState<DefensiveStyle>(team.tactics.defensiveStyle || 'Standard');
    const [tacklingStyle, setTacklingStyle] = useState<TacklingStyle>(team.tactics.tacklingStyle || 'Normal');
    const [performanceModalData, setPerformanceModalData] = useState<{ player: Player, position: TacticalPosition } | null>(null);
    const [playerToAssign, setPlayerToAssign] = useState<Player | null>(null);
    const [showTacticsHelpModal, setShowTacticsHelpModal] = useState(false);
    const [showTeamHelpModal, setShowTeamHelpModal] = useState(false);
    const pitchContainerRef = useRef<HTMLDivElement>(null);

    const assistantManagerMgmt = useAssistantManagerManagement(gameState);

    useEffect(() => {
        setFormation(team.tactics.formation);
        setMentality(team.tactics.mentality || 'Balanced');
        setLineup(team.tactics.lineup);
        setAttackingFocus(team.tactics.attackingFocus || 'Mixed');
        setPassingStyle(team.tactics.passingStyle || 'Mixed');
        setDefensiveStyle(team.tactics.defensiveStyle || 'Standard');
        setTacklingStyle(team.tactics.tacklingStyle || 'Normal');
    }, [team]);
    
    useEffect(() => {
        if (pitchContainerRef.current) {
            const container = pitchContainerRef.current;
            // Center the scroll position on load and when formation changes
            container.scrollLeft = (container.scrollWidth - container.clientWidth) / 2;
        }
    }, [formation]);
    
    const squadSelectionSuggestions = useMemo((): SquadSuggestion[] => {
        return getSquadSelectionSuggestions({ ...team, tactics: { ...team.tactics, lineup } });
    }, [team, lineup]);

    const teamStrengths = useMemo(() => {
        return calculateTeamStrengths({ ...team, tactics: { ...team.tactics, formation, lineup, mentality, attackingFocus, passingStyle, defensiveStyle, tacklingStyle } });
    }, [team, formation, lineup, mentality, attackingFocus, passingStyle, defensiveStyle, tacklingStyle]);

    const handleSuggestionClick = (position: TacticalPosition) => {
        soundService.playClick();
        setSelectedSlot(position);
    };

    const handleApplySuggestion = (suggestion: SquadSuggestion) => {
        soundService.playSuccess();
        const newLineup = { ...lineup };
        newLineup[suggestion.position] = suggestion.reserve.id;
        setLineup(newLineup);
        onUpdateTactics(formation, mentality, newLineup, attackingFocus, passingStyle, defensiveStyle, tacklingStyle);
    };

    const handleApplyAllSuggestions = () => {
        soundService.playSuccess();
        let newLineup = { ...lineup };
        const assignedPlayerIds = new Set<number>();

        // Collect already-assigned players
        Object.values(newLineup).forEach(playerId => {
            if (playerId !== null) assignedPlayerIds.add(playerId);
        });

        squadSelectionSuggestions.forEach(suggestion => {
            // Only apply suggestion if the reserve player isn't already assigned elsewhere
            if (!assignedPlayerIds.has(suggestion.reserve.id)) {
                // Remove the reserve player from any other positions first
                Object.keys(newLineup).forEach(pos => {
                    if (newLineup[pos as TacticalPosition] === suggestion.reserve.id) {
                        newLineup[pos as TacticalPosition] = null;
                    }
                });
                newLineup[suggestion.position] = suggestion.reserve.id;
                assignedPlayerIds.add(suggestion.reserve.id);
            }
        });
        setLineup(newLineup);
        onUpdateTactics(formation, mentality, newLineup, attackingFocus, passingStyle, defensiveStyle, tacklingStyle);
    };

    const handleFormationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        soundService.playClick();
        const newFormation = e.target.value;
        const newLineup = calculateBestLineup(newFormation, team.squad);
        setFormation(newFormation);
        setLineup(newLineup);
        onUpdateTactics(newFormation, mentality, newLineup, attackingFocus, passingStyle, defensiveStyle, tacklingStyle);
    };

    const handleMentalityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        soundService.playClick();
        const newMentality = e.target.value as TacticalMentality;
        setMentality(newMentality);
        onUpdateTactics(formation, newMentality, lineup, attackingFocus, passingStyle, defensiveStyle, tacklingStyle);
    };

    const handleAutoSetup = () => {
        soundService.playClick();
        // Get next fixture to compare against
        const allLeagueFixtures = gameState.fixtures.leagues[gameState.playerLeagueName]
            ? Object.values(gameState.fixtures.leagues[gameState.playerLeagueName]).flat()
            : [];
        const nextFixture = allLeagueFixtures.find(
            f => (f.week > gameState.currentWeek) && (f.home === gameState.playerTeamName || f.away === gameState.playerTeamName)
        );

        const nextOpponent = nextFixture
            ? Object.values(gameState.leagues)
                .flatMap(l => l.teams)
                .find(t => t.name === (nextFixture.home === gameState.playerTeamName ? nextFixture.away : nextFixture.home))
            : null;

        const recommendation = assistantManagerMgmt.getTacticalRecommendation(
            gameState.playerTeamName,
            nextOpponent || undefined
        );

        if (recommendation) {
            soundService.playSuccess();
            const newLineup = calculateBestLineup(recommendation.formation, team.squad);
            setFormation(recommendation.formation);
            setLineup(newLineup);
            setMentality(recommendation.mentality);
            setDefensiveStyle(recommendation.defensiveStyle);
            setPassingStyle(recommendation.passingStyle);
            setAttackingFocus(recommendation.attackingFocus);
            setTacklingStyle(recommendation.tacklingStyle);
            onUpdateTactics(recommendation.formation, recommendation.mentality, newLineup, recommendation.attackingFocus, recommendation.passingStyle, recommendation.defensiveStyle, recommendation.tacklingStyle);
            const managerName = ASSISTANT_MANAGER_LEVELS[team.assistantManager.level - 1]?.name || 'Assistant Manager';

            // Create news item for the setup change
            const newsId = Date.now().toString();
            const newsItem: NewsItem = {
                id: newsId,
                title: '📋 Tactical Setup Updated',
                message: `${managerName} has recommended a tactical setup for your next match. Formation: ${recommendation.formation}, Mentality: ${recommendation.mentality}. Reason: ${recommendation.reasoning}`,
                type: 'general',
                week: gameState.currentWeek,
                season: gameState.season,
                isRead: false,
                link: { type: 'tactics', title: 'Tactics' }
            };
            onAddNews(newsItem);

            // Show toast that's clickable to view the news
            addToast(
                `${managerName}: Tactics updated! Check Inbox for details.`,
                'success',
                () => pushView({ type: 'inbox', title: 'Inbox' })
            );
        }
    };

    const handleAttackingFocusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        soundService.playClick();
        const newFocus = e.target.value as AttackingFocus;
        setAttackingFocus(newFocus);
        onUpdateTactics(formation, mentality, lineup, newFocus, passingStyle, defensiveStyle, tacklingStyle);
    };

    const handlePassingStyleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        soundService.playClick();
        const newStyle = e.target.value as PassingStyle;
        setPassingStyle(newStyle);
        onUpdateTactics(formation, mentality, lineup, attackingFocus, newStyle, defensiveStyle, tacklingStyle);
    };

    const handleDefensiveStyleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        soundService.playClick();
        const newStyle = e.target.value as DefensiveStyle;
        setDefensiveStyle(newStyle);
        onUpdateTactics(formation, mentality, lineup, attackingFocus, passingStyle, newStyle, tacklingStyle);
    };

    const handleTacklingStyleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        soundService.playClick();
        const newStyle = e.target.value as TacklingStyle;
        setTacklingStyle(newStyle);
        onUpdateTactics(formation, mentality, lineup, attackingFocus, passingStyle, defensiveStyle, newStyle);
    };

    const handleAssignPlayer = (player: Player) => {
        soundService.playSuccess();
        if (!selectedSlot) return;

        const currentPositionOfPlayer = Object.keys(lineup).find(pos => lineup[pos as TacticalPosition] === player.id) as TacticalPosition | undefined;
        
        const newLineup = { ...lineup };

        if (currentPositionOfPlayer) {
            newLineup[currentPositionOfPlayer] = lineup[selectedSlot];
        }
        
        newLineup[selectedSlot] = player.id;
        setLineup(newLineup);
        onUpdateTactics(formation, mentality, newLineup, attackingFocus, passingStyle, defensiveStyle, tacklingStyle);
        setSelectedSlot(null);
    };

    const handleUnassignPlayer = () => {
        soundService.playError();
        if (!selectedSlot) return;
        const newLineup = { ...lineup };
        newLineup[selectedSlot] = null;
        setLineup(newLineup);
        onUpdateTactics(formation, mentality, newLineup, attackingFocus, passingStyle, defensiveStyle, tacklingStyle);
        setSelectedSlot(null);
    }
    
    const formationZones = useMemo(() => getFormationZones(formation).reverse(), [formation]);
    const getPlayerById = useCallback((id: number | null) => id === null ? null : team.squad.find(p => p.id === id) || null, [team.squad]);
    const reserves = useMemo(() => team.squad.filter(p => !Object.values(lineup).includes(p.id)).sort((a,b) => b.skill - a.skill), [team.squad, lineup]);

    const handlePlayerClick = (player: Player) => {
        soundService.playClick();
        pushView({ type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id });
    };

    const handleAssignFromReserves = (player: Player) => {
        soundService.playClick();
        setPlayerToAssign(player);
    };

    const handleSwapPlayers = (playerToReplaceId: number) => {
        soundService.playSuccess();
        if (!playerToAssign) return;

        const currentPositionOfAssignee = Object.keys(lineup).find(pos => lineup[pos as TacticalPosition] === playerToAssign.id) as TacticalPosition | undefined;
        const newLineup = { ...lineup };

        // Find position where player to replace is currently assigned
        const positionOfReplacedPlayer = Object.keys(newLineup).find(pos => newLineup[pos as TacticalPosition] === playerToReplaceId) as TacticalPosition | undefined;

        if (positionOfReplacedPlayer) {
            // Swap: put the replaced player in the assignee's old position (or bench if assignee wasn't in XI)
            if (currentPositionOfAssignee) {
                newLineup[currentPositionOfAssignee] = playerToReplaceId;
            }
            // Put the assignee in the replaced player's position
            newLineup[positionOfReplacedPlayer] = playerToAssign.id;
        }

        setLineup(newLineup);
        onUpdateTactics(formation, mentality, newLineup, attackingFocus, passingStyle, defensiveStyle, tacklingStyle);
        setPlayerToAssign(null);
    };

    return (
        <>
            <h1><FontAwesomeIcon icon={faChess} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Tactics</h1>
            <p style={{ marginBottom: '24px', opacity: 0.9 }}>
                Set your formation, mentality, and lineup. Select your best players for each position and choose your tactical approach.
            </p>

            {adsEnabled && <AdPlaceholder />}

            {squadSelectionSuggestions.length > 0 && (
                <CollapsibleCard
                    cardId="tactics-selection-suggestions"
                    title="Assistant Manager"
                    subtitle={`${squadSelectionSuggestions.length} suggestion${squadSelectionSuggestions.length === 1 ? '' : 's'}`}
                    defaultCollapsed={false}
                    headerActions={
                        <>
                            <button
                                className="button small"
                                onClick={handleApplyAllSuggestions}
                                title="Apply all suggestions"
                            >
                                Fix all
                            </button>
                        </>
                    }                    
                >
                    <div className="selection-suggestions-container">
                        {squadSelectionSuggestions.map(suggestion => (
                            <div key={suggestion.reserve.id} className="selection-suggestion-card">
                                 <div className="suggestion-info">
                                    <PlayerFace player={suggestion.reserve} teamColor={team.colors.primary} />
                                    <div>
                                        <p>
                                            Consider starting <strong>{suggestion.reserve.name}</strong> (Perf: {suggestion.reserveFinalPerf.toFixed(0)})
                                            over <span className="starter-name">{suggestion.starter.name}</span> (Perf: {suggestion.starterFinalPerf.toFixed(0)})
                                            at <button className="link-style" onClick={(e) => { e.stopPropagation(); handleSuggestionClick(suggestion.position); }}>{suggestion.position}</button>.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    className="button small primary suggestion-fix-button"
                                    onClick={() => handleApplySuggestion(suggestion)}
                                >
                                    Fix
                                </button>
                            </div>
                        ))}
                    </div>
                </CollapsibleCard>
            )}

            <CollapsibleCard
                cardId="tactics-setup"
                title="Tactics Setup"
                defaultCollapsed={false}
                headerActions={
                    <>
                        <button
                            className="button small"
                            onClick={handleAutoSetup}
                            title={`Get tactical recommendations from your ${ASSISTANT_MANAGER_LEVELS[team.assistantManager.level - 1]?.name || 'Assistant Manager'}`}
                            style={{ whiteSpace: 'normal' }}
                        >
                            Auto-Setup
                        </button>
                        <button
                            className="button small"
                            onClick={() => {
                                soundService.playClick();
                                setShowTacticsHelpModal(true);
                            }}
                            title="Learn about tactics and formations"
                        >
                            Help
                        </button>
                    </>
                }
            >
                        <div className="tactics-controls">
                            <div className="tactics-group-header">Setup</div>
                            <div className="form-group">
                                <label htmlFor="formation-select">Formation</label>
                                <select id="formation-select" value={formation} onChange={handleFormationChange}>
                                    {FORMATION_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="mentality-select">Mentality</label>
                                <select id="mentality-select" value={mentality} onChange={handleMentalityChange}>
                                    <option value="Defensive">Defensive</option>
                                    <option value="Balanced">Balanced</option>
                                    <option value="Attacking">Attacking</option>
                                </select>
                            </div>

                            <div className="tactics-group-header">Attacking</div>
                            <div className="form-group">
                                <label htmlFor="attacking-focus-select">Attacking Focus</label>
                                <select id="attacking-focus-select" value={attackingFocus} onChange={handleAttackingFocusChange}>
                                    <option value="Mixed">Mixed</option>
                                    <option value="Through Middle">Through Middle</option>
                                    <option value="Down Left Flank">Down Left Flank</option>
                                    <option value="Down Right Flank">Down Right Flank</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="passing-style-select">Passing Style</label>
                                <select id="passing-style-select" value={passingStyle} onChange={handlePassingStyleChange}>
                                    <option value="Mixed">Mixed</option>
                                    <option value="Short">Short</option>
                                    <option value="Direct">Direct</option>
                                </select>
                            </div>

                            <div className="tactics-group-header">Defensive</div>
                            <div className="form-group">
                                <label htmlFor="defensive-style-select">Defensive Style</label>
                                <select id="defensive-style-select" value={defensiveStyle} onChange={handleDefensiveStyleChange}>
                                    <option value="Standard">Standard</option>
                                    <option value="High Press">High Press</option>
                                    <option value="Deep Block">Deep Block</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="tackling-style-select">Tackling</label>
                                <select id="tackling-style-select" value={tacklingStyle} onChange={handleTacklingStyleChange}>
                                    <option value="Normal">Normal</option>
                                    <option value="Aggressive">Aggressive</option>
                                    <option value="Cautious">Cautious</option>
                                </select>
                            </div>
                        </div>
                    </CollapsibleCard>

            <CollapsibleCard
                cardId="tactics-team-setup"
                title={`Team Setup (${formation})`}
                defaultCollapsed={false}
                headerActions={
                    <>
                        <button
                            className="button small"
                            onClick={() => {
                                soundService.playClick();
                                const newLineup = calculateBestLineup(formation, team.squad);
                                setLineup(newLineup);
                                onUpdateTactics(formation, mentality, newLineup, attackingFocus, passingStyle, defensiveStyle, tacklingStyle);
                            }}
                            title="Auto-fill the best XI based on player performance"
                            style={{ whiteSpace: 'normal' }}
                        >
                            Auto-Fill Best XI
                        </button>
                        <button
                            className="button small"
                            onClick={() => {
                                soundService.playClick();
                                const newLineup: Tactics['lineup'] = {};
                                FORMATIONS[formation].forEach(pos => {
                                    newLineup[pos] = null;
                                });
                                setLineup(newLineup);
                                onUpdateTactics(formation, mentality, newLineup, attackingFocus, passingStyle, defensiveStyle, tacklingStyle);
                            }}
                            title="Remove all players from the pitch"
                        >
                            Clear All
                        </button>
                        <button
                            className="button small"
                            onClick={() => {
                                soundService.playClick();
                                setShowTeamHelpModal(true);
                            }}
                            title="Learn about team selection and player performance"
                        >
                            Help
                        </button>
                    </>
                }
            >

                        {/* Team Strengths */}
                        <div className="tactics-team-strengths-container">
                            <div className="tactics-strengths-grid">
                                <div className="tactics-strength-card" title="Goalkeeper strength - based on GK skill, form, fitness, and tactics">
                                    <FontAwesomeIcon icon={iconMap['gk']} className="tactics-strength-icon" style={{ color: 'var(--positive-color)' }} />
                                    <div className="tactics-strength-label">GK</div>
                                    <div className="tactics-strength-value">
                                        {Math.round(teamStrengths.gk)}
                                    </div>
                                </div>
                                <div className="tactics-strength-card" title="Defensive strength - based on defenders + GK, with tactical modifiers">
                                    <FontAwesomeIcon icon={iconMap['defense']} className="tactics-strength-icon" style={{ color: 'var(--info-color)' }} />
                                    <div className="tactics-strength-label">Defence</div>
                                    <div className="tactics-strength-value">
                                        {Math.round(teamStrengths.def)}
                                    </div>
                                </div>
                                <div className="tactics-strength-card" title="Midfield strength - based on midfielders' performance and tactics">
                                    <FontAwesomeIcon icon={iconMap['midfield']} className="tactics-strength-icon" style={{ color: 'var(--accent-color)' }} />
                                    <div className="tactics-strength-label">Midfield</div>
                                    <div className="tactics-strength-value">
                                        {Math.round(teamStrengths.mid)}
                                    </div>
                                </div>
                                <div className="tactics-strength-card" title="Attacking strength - based on forwards' performance and tactics">
                                    <FontAwesomeIcon icon={iconMap['attack']} className="tactics-strength-icon" style={{ color: 'var(--negative-color)' }} />
                                    <div className="tactics-strength-label">Attack</div>
                                    <div className="tactics-strength-value">
                                        {Math.round(teamStrengths.fwd)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div ref={pitchContainerRef} className="tactics-pitch-container" data-ai-target="tactics-pitch">
                        <PitchBackground currentWeek={currentWeek} className="tactics-pitch">
                            {formationZones.map((zone, i) => (
                                <div key={i} className="pitch-zone">
                                    {zone.map(pos => {
                                        const player = getPlayerById(lineup[pos] ?? null);
                                        return (
                                            <div
                                                key={`${pos}-${player?.id ?? 'empty'}`}
                                                className={`player-slot ${!player ? 'empty' : ''} ${selectedSlot === pos ? 'selected' : ''}`}
                                                onClick={() => setSelectedSlot(pos)}
                                                title={`Click to assign player to ${pos}`}
                                            >
                                                {player ? (
                                                    <>
                                                        <div className="player-slot-header">
                                                            <button
                                                                className="player-slot-name-button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handlePlayerClick(player);
                                                                }}
                                                                title={`View ${player.name}'s profile`}
                                                            >
                                                                {getLastName(player.name)}
                                                            </button>
                                                        </div>
                                                        <div className="player-slot-body">
                                                            {player.injury ? (
                                                                <div className="injury-face negative"><FontAwesomeIcon icon={faAmbulance} title={`${player.injury.type} Injury (${player.injury.duration} wks)`} /></div>
                                                            ) : player.suspension ? (
                                                                <div className="injury-face" title={`Suspended: ${player.suspension.reason} (${player.suspension.matchesRemaining} ${player.suspension.matchesRemaining === 1 ? 'match' : 'matches'})`}>🟥</div>
                                                            ) : (
                                                                <PlayerFace player={player} teamColor={team.colors.primary} />
                                                            )}
                                                        </div>
                                                        <div className="player-slot-footer">
                                                            <div className="footer-stat">
                                                                <span>{player.skill}</span>
                                                                <label>SKL</label>
                                                            </div>
                                                            <div className="footer-divider"></div>
                                                            <div className="footer-stat">
                                                                <span>{pos.startsWith('ST (') ? 'ST' : pos}</span>
                                                                <label>POS</label>
                                                            </div>
                                                        </div>
                                                        {(() => {
                                                            const basePerf = calculateMatchPerformance(player);
                                                            const penalty = calculatePositionalPenalty(player, pos);
                                                            const finalPerf = Math.round(basePerf * (1 - penalty));
                                                            const skillOnly = player.skill;
                                                            const modifier = finalPerf - skillOnly;
                                                            return modifier !== 0 && (
                                                                <div className={`match-modifier-badge ${modifier > 0 ? 'positive' : 'negative'}`} title={`Final Match Performance: ${finalPerf}`}>
                                                                    {modifier > 0 ? `+${modifier}` : `${modifier}`}
                                                                </div>
                                                            );
                                                        })()}
                                                    </>
                                                ) : (
                                                    <div className="empty-slot-content">+</div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </PitchBackground>
                    </div>

                    {/* Lineup Tables */}
                    <div className="squad-list-panel">
                        <div className="tabs">
                            <button className={`tab-button ${activeList === 'xi' ? 'active' : ''}`} onClick={() => setActiveList('xi')}>Starting XI</button>
                            <button className={`tab-button ${activeList === 'reserves' ? 'active' : ''}`} onClick={() => setActiveList('reserves')}>Reserves</button>
                        </div>

                        {activeList === 'xi' && (
                             <div className="table-wrapper">
                                <table className="table lineup-table">
                                    <thead>
                                        <tr>
                                            <th>Pos</th>
                                            <th>Player</th>
                                            <th>Detail</th>
                                            <th>Foot</th>
                                            <th>Match Perf.</th>
                                            <th>Fit</th>
                                            <th>Morale</th>
                                            <th>Form</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {FORMATIONS[formation].map(pos => {
                                            const player = getPlayerById(lineup[pos] ?? null);
                                            const positionDetail = player ? TACTICAL_POSITION_DETAILS[pos] : null;
                                            return (
                                                <tr key={pos}>
                                                    <td>
                                                        <button 
                                                            className="link-style" 
                                                            onClick={() => { soundService.playClick(); setSelectedSlot(pos); }} 
                                                            style={{fontWeight: 'bold', textTransform: 'uppercase'}}
                                                            title={`Assign player to ${pos}`}
                                                        >
                                                            {pos.startsWith('ST (') ? 'ST' : pos}
                                                        </button>
                                                    </td>
                                                    <td>
                                                        {player ? (
                                                            <button className="link-style" onClick={() => handlePlayerClick(player)}>
                                                                {player.injury && <FontAwesomeIcon icon={faAmbulance} className="negative" title={`${player.injury.type} Injury (${player.injury.duration} wks)`} style={{ marginRight: '4px' }} />}
                                                                {player.suspension && <span className="injury-indicator" title={`Suspended: ${player.suspension.reason} (${player.suspension.matchesRemaining} ${player.suspension.matchesRemaining === 1 ? 'match' : 'matches'})`}>🟥 </span>}
                                                                {player.name}
                                                            </button>
                                                        ) : (
                                                            <button className="link-style text-secondary" onClick={() => { soundService.playClick(); setSelectedSlot(pos); }}>
                                                                Empty
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td title={positionDetail?.category}>{positionDetail?.category}</td>
                                                    <td title={player?.favoredFoot}>{player?.favoredFoot.charAt(0)}</td>
                                                    <td>
                                                        {player && (() => {
                                                            const basePerf = calculateMatchPerformance(player);
                                                            const penalty = calculatePositionalPenalty(player, pos);
                                                            const finalPerf = Math.round(basePerf * (1 - penalty));
                                                            const penaltyText = penalty > 0 ? `(-${Math.round(penalty * 100)}%)` : '';
                                                            return (
                                                                <button className="link-style" onClick={(e) => { e.stopPropagation(); setPerformanceModalData({ player, position: pos }); }}>
                                                                    {finalPerf} {penalty > 0 && <span className="negative" style={{fontSize: '0.8em', marginLeft: '4px'}}>{penaltyText}</span>}
                                                                </button>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td>{player && <FitnessBadge fitness={player.fitness} />}</td>
                                                    <td>{player && <MoraleBadge morale={player.morale} />}</td>
                                                    <td>
                                                        {player && (
                                                            player.form > 0 ? <span className="positive">+{player.form}</span> :
                                                            player.form < 0 ? <span className="negative">{player.form}</span> :
                                                            <span className="text-secondary">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {activeList === 'reserves' && (
                             <div className="table-wrapper">
                                <table className="table">
                                    <thead><tr><th>Player</th><th>Pos</th><th>Detail</th><th>Foot</th><th>Match Perf.</th><th>Fit</th><th>Morale</th><th>Form</th><th>Action</th></tr></thead>
                                    <tbody>
                                        {reserves.map(p => (
                                            <tr key={p.id}>
                                                <td className="clickable-row" onClick={() => handlePlayerClick(p)}>
                                                    {p.injury && <FontAwesomeIcon icon={faAmbulance} className="negative" title={`${p.injury.type} Injury (${p.injury.duration} wks)`} style={{ marginRight: '4px' }} />}
                                                    {p.name}
                                                </td>
                                                <td className="clickable-row" onClick={() => handlePlayerClick(p)}>{p.position}</td>
                                                <td className="clickable-row" onClick={() => handlePlayerClick(p)} title={p.favoredPositionCategory}>{p.favoredPositionCategory}</td>
                                                <td className="clickable-row" onClick={() => handlePlayerClick(p)} title={p.favoredFoot}>{p.favoredFoot.charAt(0)}</td>
                                                <td>
                                                    <button className="link-style" onClick={(e) => { e.stopPropagation(); setPerformanceModalData({ player: p, position: 'RES' }); }}>
                                                        {calculateMatchPerformance(p)}
                                                    </button>
                                                </td>
                                                <td className="clickable-row" onClick={() => handlePlayerClick(p)}><FitnessBadge fitness={p.fitness} /></td>
                                                <td className="clickable-row" onClick={() => handlePlayerClick(p)}><MoraleBadge morale={p.morale} /></td>
                                                <td className="clickable-row" onClick={() => handlePlayerClick(p)}>
                                                    {p.form > 0 ? <span className="positive">+{p.form}</span> :
                                                    p.form < 0 ? <span className="negative">{p.form}</span> :
                                                    <span className="text-secondary">-</span>}
                                                </td>
                                                <td>
                                                    <button className="button small" onClick={(e) => { e.stopPropagation(); handleAssignFromReserves(p); }}>
                                                        Assign
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
            </CollapsibleCard>

            {selectedSlot && (
                <PlayerAssignmentModal
                    isOpen={!!selectedSlot}
                    onClose={() => setSelectedSlot(null)}
                    onAssignPlayer={handleAssignPlayer}
                    onUnassign={handleUnassignPlayer}
                    team={team}
                    selectedSlot={selectedSlot}
                    gameState={gameState}
                />
            )}
            {playerToAssign && (
                <PlayerAssignmentModal
                    isOpen={!!playerToAssign}
                    onClose={() => setPlayerToAssign(null)}
                    playerToAssign={playerToAssign}
                    onSwapPlayers={handleSwapPlayers}
                    getPlayerById={getPlayerById}
                    team={team}
                    gameState={gameState}
                />
            )}
            {performanceModalData && (
                <MatchPerformanceDetailModal
                    isOpen={!!performanceModalData}
                    onClose={() => setPerformanceModalData(null)}
                    player={performanceModalData.player}
                    tacticalPosition={performanceModalData.position}
                />
            )}
            <TacticsSetupHelpModal
                isOpen={showTacticsHelpModal}
                onClose={() => {
                    soundService.playClick();
                    setShowTacticsHelpModal(false);
                }}
            />
            <TeamSetupHelpModal
                isOpen={showTeamHelpModal}
                onClose={() => {
                    soundService.playClick();
                    setShowTeamHelpModal(false);
                }}
            />
        </>
    );
};